import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {AppParametricSelector, makeMapStateToProps} from '../../../../helpers/reselectHelpers';
import {IntegrationModel} from '../../../../models/api/integrationModel';
import {
  IntegrationTypeModel,
  IntegrationTypesSupportedTypesEnum,
} from '../../../../models/api/integrationTypesModel';
import {AppState} from '../../../../store/appState';
import {getLegacySidePanelEntryPoints} from '../../../../store/data/applications/legacyApplicationsSelectors';
import {getIntegrationsByType} from '../../../../store/data/dataSelectors';
import {makeGetIsIntegrationDisabledViaBilling} from '../../../../store/data/integrations/integrationSelectors';
import {makeGetIsIntegrationTypeEnabled} from '../../../../store/data/integrationTypes/integrationTypesSelectors';
import {getUser} from '../../../../store/data/teammates/teammatesSelectors';
import {SettingsAppsDetailsSidepanel} from './settingsAppsDetailsSidepanel';

/*
 * Props.
 */

interface SettingsAppsDetailsSidepanelContainerProps {
  integrationType: IntegrationTypeModel;
}

/*
 * Selectors.
 */

function getIntegrationType(_state: AppState, props: SettingsAppsDetailsSidepanelContainerProps) {
  return props.integrationType;
}

function getIntegrationTypeId(_state: AppState, props: SettingsAppsDetailsSidepanelContainerProps) {
  return props.integrationType.id;
}

function makeGetIntegration(getIntegrationId: AppParametricSelector<any, string>) {
  return createSelector(
    [getIntegrationId, getIntegrationsByType],
    (integrationId, integrations) => integrations[integrationId],
  );
}

function makeGetIntegrationFrameId(getIntegration: AppParametricSelector<any, IntegrationModel>) {
  return createSelector(
    [getIntegrationType, getLegacySidePanelEntryPoints, getIntegration],
    (integrationType, entryPoints, integration) => {
      // If the integration is only a channel or other, we will not have a plugin frame id.
      if (
        integrationType.supportedTypes.length === 1 &&
        (integrationType.supportedTypes.includes(IntegrationTypesSupportedTypesEnum.CHANNEL) ||
          integrationType.supportedTypes.includes(IntegrationTypesSupportedTypesEnum.OTHER))
      ) {
        return undefined;
      }

      // If we do not have an integration, we do not have a plugin frame id.
      if (!integration) {
        return undefined;
      }

      // If the integration has a plugin natively we can just set this as the frame id to render.
      if (integration.hasPlugin) {
        return integration.frameId;
      }

      // Check for entrypoints for plugins.
      const entryPoint = entryPoints.find((e) => e.applicationId === integration.id);
      if (entryPoint) {
        return entryPoint.frameId;
      }

      return undefined;
    },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SettingsAppsDetailsSidepanelContainerProps>()(() => {
  const getIntegration = makeGetIntegration(getIntegrationTypeId);
  return {
    integration: getIntegration,
    integrationFrameId: makeGetIntegrationFrameId(getIntegration),
    isIntegrationTypeEnabled: makeGetIsIntegrationTypeEnabled(getIntegrationTypeId),
    isIntegrationDisabledViaBilling: makeGetIsIntegrationDisabledViaBilling(getIntegrationTypeId),
    user: getUser,
  };
});

export const SettingsAppsDetailsSidepanelContainer = connect(mapStateToProps)(SettingsAppsDetailsSidepanel);
